import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  finalize,
  first,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import {
  MapFilterItem,
  MapFilterType,
  MapFilterValueType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { typeEntityFieldName } from 'app/shared/constants/search-results.constant';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import {
  EntityModelLike,
  EntityTypeModelLike,
} from 'app/shared/models/entity.model';
import { EntityService } from 'app/shared/services/entity.service';
import { MapFilterManager } from '../../map-filter.manager';

@Component({
  selector: 'app-entity-type-filter-form',
  templateUrl: 'entity-type-filter-form.component.html',
  styleUrls: ['entity-type-filter-form.component.scss'],
})
export class EntityTypeFilterFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  entityTypeList: EntityTypeModelLike[];

  private _isAllSelected = false;

  get isAllSelected() {
    return this._isAllSelected;
  }

  set isAllSelected(value) {
    value = !!value;
    if (this._isAllSelected !== value) {
      this._isAllSelected = value;

      this.entityTypeList?.forEach((item) => {
        this.setEntityTypeSelected(item, value);
      });

      this.handleAllSelectedItemRef();
    }
  }

  private filterList: MapFilterItem[] = [];
  private newFiltersMap = new Map<string, EntityTypeModelLike>();
  private filterKeySetRemoved = new Set<string>();

  private initialItemsSelectedSet = new Set<string>();
  private itemsSelectedSet = new Set<string>();

  private filtersSubscription: Subscription;

  private entityTypeListSubscription: Subscription;

  searchFilterTextSubject = new BehaviorSubject<string>('');
  searchFilterTextObservable = this.searchFilterTextSubject.pipe(
    debounceTime(300)
  );

  searchFilterValueListExpr = (
    item: EntityTypeModelLike,
    filterValue: string
  ) => {
    if (filterValue) {
      return item.name?.toLowerCase()?.includes(filterValue.toLowerCase());
    }

    return true;
  };

  private lifecycle = new Subject<void>();

  constructor(
    private entityService: EntityService,
    private mapFilterManager: MapFilterManager,
    private loadingController: LoadingController
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.visible) {
      this.handleEntityTypeList();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onShowingInternal() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.mapFilterManager.onFiltersChanged
      .pipe(
        takeUntil(this.visibleChange.pipe(first((x) => !x))),
        takeUntil(this.lifecycle)
      )
      .subscribe((list) => {
        this.filterList = list.filter(
          (x) => x.type === MapFilterType.EntityType
        );

        this.handleValues();
      });
  }

  onHiddenInternal() {
    this.filtersSubscription?.unsubscribe();

    this.newFiltersMap.clear();
    this.filterKeySetRemoved.clear();
    this.itemsSelectedSet.clear();
    this.initialItemsSelectedSet.clear();
    this.isAllSelected = false;
  }

  onBtnApplyClick() {
    let adds: MapFilterItem[];
    let deletes: string[];

    if (this.isAllSelected) {
      if (
        !this.filterList?.some(
          (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
        )
      ) {
        adds = [
          new MapFilterItem({
            type: MapFilterType.EntityType,
            text: 'Todos',
            field: typeEntityFieldName,
            valueAsStr: MapFilterItem.DEFAULT_VALUE_ALL,
            valueType: MapFilterValueType.String,
          }),
        ];
      }

      deletes = Array.from(this.initialItemsSelectedSet.values());
    } else {
      adds = Array.from(this.newFiltersMap.values(), (item) => {
        return new MapFilterItem({
          id: item.id,
          type: MapFilterType.EntityType,
          text: item.name,
          field: typeEntityFieldName,
          valueAsStr: item.id,
          valueType: MapFilterValueType.String,
        });
      });
      deletes = Array.from(this.filterKeySetRemoved.values());
    }

    if (deletes.length && adds[0]?.value !== MapFilterItem.DEFAULT_VALUE_ALL) {
      const filterKeys = this.mapFilterManager.listFilterKeysBy(
        (x) =>
          x.type === MapFilterType.Entity &&
          x.value !== MapFilterItem.DEFAULT_VALUE_ALL
      );

      deletes = deletes.concat(filterKeys);
    }

    this.mapFilterManager.applyEditsFilters({
      adds,
      deletes,
    });

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnClearClick() {
    this.resetValues();
  }

  isEntityTypeSelected(item: EntityModelLike): boolean {
    return this.itemsSelectedSet.has(item.id);
  }

  setEntityTypeSelected(item: EntityTypeModelLike, selected: boolean) {
    if (selected) {
      this.itemsSelectedSet.add(item.id);
      this.filterKeySetRemoved.delete(item.id);

      if (!this.initialItemsSelectedSet.has(item.id)) {
        this.newFiltersMap.set(item.id, item);
      }
    } else {
      this.itemsSelectedSet.delete(item.id);

      if (this.initialItemsSelectedSet.has(item.id)) {
        this.filterKeySetRemoved.add(item.id);
      }

      this.newFiltersMap.delete(item.id);

      if (this.isAllSelected) {
        this._isAllSelected = false;
        this.handleAllSelectedItemRef();
      }
    }
  }

  private handleAllSelectedItemRef() {
    const itemRef = this.filterList?.find(
      (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
    );
    if (itemRef) {
      this.setEntityTypeSelected(itemRef, this.isAllSelected);
    }
  }

  private handleValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();

    this.initialItemsSelectedSet = new Set(
      this.filterList?.map((x) => {
        if (x.value === MapFilterItem.DEFAULT_VALUE_ALL) {
          return x.id;
        }

        return x.value;
      }) ?? []
    );
    this.itemsSelectedSet = new Set(this.initialItemsSelectedSet);

    this.isAllSelected = this.filterList?.some(
      (x) => x.value === MapFilterItem.DEFAULT_VALUE_ALL
    );
  }

  private resetValues() {
    this.newFiltersMap.clear();
    this.filterKeySetRemoved = new Set(this.initialItemsSelectedSet);

    this.itemsSelectedSet.clear();
    this.isAllSelected = false;
  }

  private handleEntityTypeList() {
    if (this.entityTypeList) {
      return;
    }

    this.entityTypeListSubscription?.unsubscribe();
    if (this.visible) {
      this.loadingController.show();
      this.entityTypeListSubscription = this.entityService
        .listAllEntityTypes()
        .pipe(
          finalize(() => {
            this.loadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe((list) => {
          this.entityTypeList = list;

          this.handleValues();
        });
    }
  }
}
